import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { XCircle, CheckCircle2, PenLine } from "lucide-react";
import DateCurrentInput from "../inputs/DateCurrentInput";
import { Ingredient, User } from "../types/Types";

interface IngredientSignaturePanelProps {
  ingredient: Ingredient | null;
  isCreating: boolean;
  isSigned: boolean;
  preparedUserId: string;
  setPreparedUserId: (v: string) => void;
  preparedDate: string;
  setPreparedDate: (v: string) => void;
  approvedUserId: string;
  setApprovedUserId: (v: string) => void;
  approvedDate: string;
  setApprovedDate: (v: string) => void;
  usersList: User[];
  setHasChanges: (v: boolean) => void;
  setSignField: (v: "prepared" | "approved" | null) => void;
  onRemoveSignature: (field: "prepared" | "approved") => void;
}

const labelCls = "block text-xs font-semibold text-gray-700 mb-1.5";
const selectCls = (enabled: boolean) =>
  `w-full h-8 px-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
    enabled ? "bg-white" : "bg-gray-50 cursor-not-allowed"
  }`;

const dateWrapperClass =
  "flex-1 relative [&_.react-datepicker-wrapper]:w-full [&_input]:w-full [&_input]:px-2 [&_input]:py-1 [&_input]:h-8 [&_input]:text-xs [&_input]:border [&_input]:rounded-lg [&_input]:text-gray-800 [&_input]:bg-white [&_input]:border-gray-300 [&_input]:focus:ring-blue-500 [&_input]:focus:border-blue-500";

const OSSignaturePanel: React.FC<IngredientSignaturePanelProps> = ({
  ingredient,
  isCreating,
  isSigned,
  preparedUserId,
  setPreparedUserId,
  preparedDate,
  setPreparedDate,
  approvedUserId,
  setApprovedUserId,
  approvedDate,
  setApprovedDate,
  usersList,
  setHasChanges,
  setSignField,
  onRemoveSignature,
}) => {
  const { t } = useTranslation();

  const renderUserOptions = () =>
    usersList.map((u) => {
      const name = `${u.firstName || ""} ${u.lastName || ""}`.trim() || (u as any).userName || (u as any).email || u._id;
      return (
        <option key={u._id} value={u._id}>
          {name}
        </option>
      );
    });

  const preparedSig = ingredient?.prepared?.signature;
  const approvedSig = ingredient?.approved?.signature;

  return (
    <div className="grid grid-cols-2 gap-3 flex-1 items-start">
      {/* Prepared */}
      <div>
        <label className={labelCls}>{t("qualDoc.prepared") || "Prepared"}</label>
        <select
          value={preparedUserId}
          onChange={(e) => {
            setPreparedUserId(e.target.value);
            setHasChanges(true);
          }}
          disabled={isSigned}
          className={selectCls(!isSigned)}
        >
          <option value="">{t("qualDoc.selectUser") || "Select user"}</option>
          {renderUserOptions()}
        </select>
        <div className="flex items-center gap-1.5 mt-1">
          <div className={dateWrapperClass}>
            <DateCurrentInput
              value={preparedDate}
              onChange={(val) => {
                setPreparedDate(val);
                setHasChanges(true);
              }}
              disabled={isSigned}
            />
          </div>
          <button
            type="button"
            title={preparedDate ? t("qualDoc.clearPrepared") || "Clear" : t("qualDoc.markAsPrepared") || "Set today"}
            onClick={() => {
              if (preparedDate) {
                setPreparedDate("");
                setPreparedUserId("");
                setHasChanges(true);
              } else {
                setPreparedDate(moment().format("DD.MM.YYYY"));
                setHasChanges(true);
              }
            }}
            disabled={isSigned}
            className={`w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-lg border transition-all ${
              preparedDate
                ? "border-red-200 text-red-500 hover:bg-red-50"
                : "border-green-200 text-green-600 hover:bg-green-50"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {preparedDate ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          </button>
        </div>
        {preparedSig ? (
          <div className="mt-1 flex items-center gap-2 h-8 px-2 bg-green-50 border border-green-200 rounded-lg">
            <img src={preparedSig} alt="sig" className="h-5 object-contain" />
            {!approvedSig && <button
              type="button"
              onClick={() => onRemoveSignature("prepared")}
              className="ml-auto text-red-400 hover:text-red-600"
              title={t("common.remove") || "Remove"}
            >
              <XCircle className="w-4 h-4" />
            </button>}
          </div>
        ) : (
          <button
            type="button"
            disabled={!ingredient || isCreating}
            onClick={() => setSignField("prepared")}
            className="mt-1 flex items-center gap-2 h-8 px-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full"
          >
            <PenLine className="w-3.5 h-3.5 text-blue-500 shrink-0" />
            <span className="text-xs text-blue-600 font-medium truncate">
              {t("qualDoc.signDocument") || "Sign"}
            </span>
          </button>
        )}
      </div>

      {/* Approved */}
      <div>
        <label className={labelCls}>{t("qualDoc.approved") || "Approved"}</label>
        <select
          value={approvedUserId}
          onChange={(e) => {
            setApprovedUserId(e.target.value);
            setHasChanges(true);
          }}
          disabled={isSigned}
          className={selectCls(!isSigned)}
        >
          <option value="">{t("qualDoc.selectUser") || "Select user"}</option>
          {renderUserOptions()}
        </select>
        <div className="flex items-center gap-1.5 mt-1">
          <div className={dateWrapperClass}>
            <DateCurrentInput
              value={approvedDate}
              onChange={(val) => {
                setApprovedDate(val);
                setHasChanges(true);
              }}
              disabled={isSigned}
            />
          </div>
          <button
            type="button"
            title={approvedDate ? t("qualDoc.clearApproved") || "Clear" : t("qualDoc.markAsApproved") || "Set today"}
            onClick={() => {
              if (approvedDate) {
                setApprovedDate("");
                setApprovedUserId("");
                setHasChanges(true);
              } else {
                setApprovedDate(moment().format("DD.MM.YYYY"));
                setHasChanges(true);
              }
            }}
            disabled={isSigned}
            className={`w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-lg border transition-all ${
              approvedDate
                ? "border-red-200 text-red-500 hover:bg-red-50"
                : "border-green-200 text-green-600 hover:bg-green-50"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {approvedDate ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          </button>
        </div>
        {approvedSig ? (
          <div className="mt-1 flex items-center gap-2 h-8 px-2 bg-green-50 border border-green-200 rounded-lg">
            <img src={approvedSig} alt="sig" className="h-5 object-contain" />
          </div>
        ) : (
          <button
            type="button"
            disabled={!ingredient || isCreating || !preparedSig}
            onClick={() => setSignField("approved")}
            className="mt-1 flex items-center gap-2 h-8 px-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full"
            title={!preparedSig ? t("qualDoc.mustBePreparedFirst") || "Must be prepared first" : ""}
          >
            <PenLine className="w-3.5 h-3.5 text-blue-500 shrink-0" />
            <span className="text-xs text-blue-600 font-medium truncate">
              {t("qualDoc.signDocument") || "Sign"}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default OSSignaturePanel;
